import React from 'react';

// Phases that include the extended playbook and pushback states
export type SessionPhase =
  | 'connecting'
  | 'waiting'
  | 'delivering'
  | 'recording'
  | 'processing'
  | 'evaluating'
  | 'followup'
  | 'completed';

interface SessionStatusBadgeProps {
  phase: SessionPhase;
  /** Item progress label, e.g. "3 / 7" */
  progressLabel?: string;
}

interface PhaseConfig {
  label: string;
  colorClass: string;
  dotClass: string;
  animate: 'pulse' | 'spin' | 'none';
}

const PHASE_CONFIG: Record<SessionPhase, PhaseConfig> = {
  connecting: {
    label: 'Connecting...',
    colorClass: 'bg-gray-100 text-gray-700 border-gray-300',
    dotClass: 'bg-gray-400',
    animate: 'pulse',
  },
  waiting: {
    label: 'Ready',
    colorClass: 'bg-gray-100 text-gray-700 border-gray-300',
    dotClass: 'bg-gray-400',
    animate: 'none',
  },
  delivering: {
    label: 'Customer Speaking...',
    colorClass: 'bg-blue-100 text-blue-800 border-blue-300',
    dotClass: 'bg-blue-500',
    animate: 'pulse',
  },
  recording: {
    label: 'Your Turn — Speak Now',
    colorClass: 'bg-green-100 text-green-800 border-green-300',
    dotClass: 'bg-green-500',
    animate: 'pulse',
  },
  processing: {
    label: 'Processing...',
    colorClass: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    dotClass: 'bg-yellow-500',
    animate: 'spin',
  },
  evaluating: {
    label: 'Processing Response...',
    colorClass: 'bg-purple-100 text-purple-800 border-purple-300',
    dotClass: 'bg-purple-500',
    animate: 'spin',
  },
  followup: {
    label: 'Customer Responding...',
    colorClass: 'bg-blue-100 text-blue-800 border-blue-300',
    dotClass: 'bg-blue-500',
    animate: 'pulse',
  },
  completed: {
    label: 'Session Complete',
    colorClass: 'bg-green-100 text-green-800 border-green-300',
    dotClass: 'bg-green-500',
    animate: 'none',
  },
};

const SessionStatusBadge: React.FC<SessionStatusBadgeProps> = ({
  phase,
  progressLabel,
}) => {
  const cfg = PHASE_CONFIG[phase];
  const effectiveLabel = cfg.label;

  const dotAnimation =
    cfg.animate === 'pulse'
      ? 'animate-pulse'
      : cfg.animate === 'spin'
      ? 'animate-spin'
      : '';

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${cfg.colorClass}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dotClass} ${dotAnimation}`} />
      <span>{effectiveLabel}</span>
      {progressLabel && (
        <>
          <span className="opacity-40">|</span>
          <span className="text-xs opacity-70">{progressLabel}</span>
        </>
      )}
    </div>
  );
};

export default SessionStatusBadge;
